import { ENV } from '../env';

export type LogLevel = "trace" | "debug" | "info" | "warn" | "error";

type LoggerConfig = {
  level: LogLevel;
  enabled: boolean;
};

const levelRank: Record<LogLevel, number> = {
  trace: 10,
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
};

const getDefaultLevel = (): LogLevel => {
  const envLevel = (ENV.LOG_LEVEL || "info").toLowerCase();
  if (["trace", "debug", "info", "warn", "error"].includes(envLevel)) {
    return envLevel as LogLevel;
  }
  return "info";
};

const initialConfig: LoggerConfig = {
  level: getDefaultLevel(),
  enabled: (ENV.ENABLE_LOGGING || "true").toLowerCase() !== "false",
};

class Logger {
  private namespace?: string;
  private config: LoggerConfig;

  constructor(namespace?: string, config: Partial<LoggerConfig> = {}) {
    this.namespace = namespace;
    this.config = { ...initialConfig, ...config } as LoggerConfig;
  }

  public withNamespace(namespace: string) {
    return new Logger(namespace, this.config);
  }

  public setLevel(level: LogLevel) {
    this.config.level = level;
  }

  public setEnabled(enabled: boolean) {
    this.config.enabled = enabled;
  }

  private shouldLog(level: LogLevel) {
    return (
      this.config.enabled && levelRank[level] >= levelRank[this.config.level]
    );
  }

  private formatPrefix(level: LogLevel) {
    const ns = this.namespace ? `[${this.namespace}] ` : "";
    return `${ns}${level.toUpperCase()}`;
  }

  private log(level: LogLevel, ...args: any[]) {
    if (!this.shouldLog(level)) return;
    const prefix = this.formatPrefix(level);
    const time = new Date().toISOString();
    const payload = [`${time} ${prefix}:`, ...args];
    switch (level) {
      case "trace":
      case "debug":
        // eslint-disable-next-line no-console
        console.debug(...payload);
        break;
      case "info":
        // eslint-disable-next-line no-console
        console.info(...payload);
        break;
      case "warn":
        // eslint-disable-next-line no-console
        console.warn(...payload);
        break;
      case "error":
        // eslint-disable-next-line no-console
        console.error(...payload);
        break;
    }
  }

  public trace(...args: any[]) {
    this.log("trace", ...args);
  }
  public debug(...args: any[]) {
    this.log("debug", ...args);
  }
  public info(...args: any[]) {
    this.log("info", ...args);
  }
  public warn(...args: any[]) {
    this.log("warn", ...args);
  }
  public error(...args: any[]) {
    this.log("error", ...args);
  }
}

export const createLogger = (
  namespace?: string,
  config?: Partial<LoggerConfig>
) => new Logger(namespace, config);

export const appLogger = createLogger("ZEOS");
